import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  searchDeskAttendees,
  recordDeskHandover,
  reprintDeskBadge,
  updateDeskAttendee,
  registerWalkIn,
  refundAttendeePass,
  DeskAttendeeResult,
} from '../lib/api';
import { BadgeCard4x6 } from './BadgeCard4x6';
import {
  Search,
  Ticket,
  Printer,
  ExternalLink,
  CheckCircle2,
  Clock,
  Building2,
  Mail,
  Phone,
  Copy,
  Check,
  RefreshCw,
  UserCheck,
  ShieldCheck,
  X,
  FileCheck2,
  RotateCcw,
  UserPlus,
  Edit3,
  PackageCheck,
  AlertCircle,
  AlertTriangle,
  Sparkles,
  Tag,
  CreditCard,
} from 'lucide-react';

interface AdminHelpdeskProps {
  adminKey: string;
}

export function AdminHelpdesk({ adminKey }: AdminHelpdeskProps) {
  const [searchInput, setSearchInput] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState('');
  const [filterRole, setFilterRole] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'checked_in' | 'pending'>('all');
  const [filterKit, setFilterKit] = useState<'all' | 'collected' | 'pending'>('all');
  const [copiedToken, setCopiedToken] = useState<string | null>(null);

  // Modals state
  const [selectedAttendeeForBadge, setSelectedAttendeeForBadge] = useState<any | null>(null);
  const [reprintAttendee, setReprintAttendee] = useState<DeskAttendeeResult | null>(null);
  const [reprintReason, setReprintReason] = useState<'lost' | 'damaged' | 'spelling_fix' | 'role_change'>('damaged');
  const [reprintNotes, setReprintNotes] = useState('');

  const [editAttendee, setEditAttendee] = useState<DeskAttendeeResult | null>(null);
  const [editFullName, setEditFullName] = useState('');
  const [editCompanyName, setEditCompanyName] = useState('');
  const [editJobTitle, setEditJobTitle] = useState('');

  const [isWalkInOpen, setIsWalkInOpen] = useState(false);
  const [walkInForm, setWalkInForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    companyName: '',
    jobTitle: '',
    primaryRole: 'trade_visitor',
    ticketType: 'expo',
    paymentMethod: 'pos',
    paymentReference: '',
    gstNumber: '',
  });

  const queryClient = useQueryClient();

  // Search Desk Attendees
  const {
    data: deskData,
    isLoading,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['admin-desk-search', adminKey, submittedQuery],
    queryFn: () => searchDeskAttendees(submittedQuery, adminKey),
    enabled: !!adminKey,
    staleTime: 5000,
  });

  const attendees: DeskAttendeeResult[] = deskData?.attendees || [];

  // Handover Mutation
  const handoverMutation = useMutation({
    mutationFn: (payload: { attendeeId: string; delegateKitIssued?: boolean; vipKitIssued?: boolean }) =>
      recordDeskHandover(payload, adminKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-desk-search'] });
    },
    onError: (err: any) => {
      alert(err.message || 'Failed to record handover');
    },
  });

  // Reprint Mutation
  const reprintMutation = useMutation({
    mutationFn: (payload: { attendeeId: string; reason: 'lost' | 'damaged' | 'spelling_fix' | 'role_change'; notes?: string }) =>
      reprintDeskBadge(payload, adminKey),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['admin-desk-search'] });
      // Launch badge preview with new badgeCode
      const currentAtt = attendees.find((a) => a.attendee.id === variables.attendeeId);
      if (currentAtt) {
        setSelectedAttendeeForBadge({
          id: currentAtt.attendee.id,
          fullName: currentAtt.attendee.fullName,
          companyName: currentAtt.attendee.companyName,
          jobTitle: currentAtt.attendee.jobTitle,
          avatarUrl: (currentAtt.attendee as any).avatarUrl || null,
          badgeCode: data.badgeCode,
          qrToken: data.badgeCode,
          primaryRole: currentAtt.theme?.role || currentAtt.attendee.primaryRole || currentAtt.attendee.tier,
          roles: currentAtt.roles?.map((r) => r.role) || [],
          stallNumber: (currentAtt.roles as any[])?.find((r) => r.stallNumber)?.stallNumber || null,
          nfcTagId: currentAtt.activeCredential?.nfcTagId || null,
          ticketType: currentAtt.attendee.ticketType,
          tier: currentAtt.attendee.tier,
          theme: currentAtt.theme,
          paymentStatus: currentAtt.attendee.paymentStatus,
        });
      }
      setReprintAttendee(null);
      setReprintNotes('');
    },
    onError: (err: any) => {
      alert(err.message || 'Failed to reprint badge');
    },
  });

  // Update Attendee Mutation
  const updateAttendeeMutation = useMutation({
    mutationFn: (payload: { attendeeId: string; fullName?: string; companyName?: string; jobTitle?: string }) =>
      updateDeskAttendee(payload, adminKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-desk-search'] });
      setEditAttendee(null);
    },
    onError: (err: any) => {
      alert(err.message || 'Failed to update attendee');
    },
  });

  // Walk-in Registration Mutation
  const walkInMutation = useMutation({
    mutationFn: (payload: any) => registerWalkIn(payload, adminKey),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['admin-desk-search'] });
      setIsWalkInOpen(false);
      // Auto-preview new 4x6 badge
      if (data.attendee && data.credential) {
        setSelectedAttendeeForBadge({
          id: data.attendee.id,
          fullName: data.attendee.fullName,
          companyName: data.attendee.companyName,
          jobTitle: data.attendee.jobTitle,
          avatarUrl: data.attendee.avatarUrl,
          badgeCode: data.credential.badgeCode,
          qrToken: data.credential.badgeCode,
          primaryRole: data.attendee.primaryRole || data.attendee.tier,
          roles: [data.attendee.primaryRole].filter(Boolean),
          nfcTagId: data.credential.nfcTagId || null,
          ticketType: data.attendee.ticketType,
          tier: data.attendee.tier,
          paymentStatus: data.attendee.paymentStatus || 'paid',
        });
      }
      setWalkInForm({
        fullName: '',
        email: '',
        phone: '',
        companyName: '',
        jobTitle: '',
        primaryRole: 'trade_visitor',
        ticketType: 'expo',
        paymentMethod: 'pos',
        paymentReference: '',
        gstNumber: '',
      });
    },
    onError: (err: any) => {
      alert(err.message || 'Failed to register walk-in attendee');
    },
  });

  // Refund / Void Pass Mutation
  const refundMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      refundAttendeePass(id, adminKey, reason),
    onSuccess: (data) => {
      alert(data.message);
      queryClient.invalidateQueries({ queryKey: ['admin-desk-search'] });
      queryClient.invalidateQueries({ queryKey: ['admin-metrics'] });
    },
    onError: (err: any) => {
      alert(err.message || 'Failed to refund pass');
    },
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmittedQuery(searchInput.trim());
  };

  const handleCopy = (token: string) => {
    navigator.clipboard.writeText(token);
    setCopiedToken(token);
    setTimeout(() => setCopiedToken(null), 2500);
  };

  // Filter list locally
  const filteredList = attendees.filter((item) => {
    const a = item.attendee;
    const isOrganiserFilter = filterRole === 'organiser';
    const roleMatch =
      filterRole === 'all' ||
      a.primaryRole === filterRole ||
      a.tier === filterRole ||
      item.roles?.some((r) => r.role === filterRole) ||
      (isOrganiserFilter &&
        (a.primaryRole === 'organizer' ||
          item.roles?.some((r) => r.role === 'organizer')));

    const statusMatch =
      filterStatus === 'all'
        ? true
        : filterStatus === 'checked_in'
        ? !!a.checkedInAt
        : !a.checkedInAt;

    const kitMatch =
      filterKit === 'all'
        ? true
        : filterKit === 'collected'
        ? item.handover?.badgeHandedOver
        : !item.handover?.badgeHandedOver;

    return roleMatch && statusMatch && kitMatch;
  });

  const openEditModal = (item: DeskAttendeeResult) => {
    setEditAttendee(item);
    setEditFullName(item.attendee.fullName);
    setEditCompanyName(item.attendee.companyName || '');
    setEditJobTitle(item.attendee.jobTitle || '');
  };

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-white border border-slate-200/90 rounded-2xl p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-xs">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="p-1.5 rounded-lg bg-[#0A3D27]/10 text-[#0A3D27]">
              <Ticket className="w-4 h-4" />
            </span>
            <h3 className="text-base font-black text-slate-900">Registration Desk & Reception Console</h3>
          </div>
          <p className="text-xs text-slate-500">
            JWCC Pavilion 3 • Manage attendee badge handovers, kit distribution, name corrections, badge reprints, and on-site walk-ins.
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            onClick={() => setIsWalkInOpen(true)}
            className="flex items-center gap-1.5 px-3.5 py-1.5 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-xl text-xs font-bold transition-colors cursor-pointer shadow-xs"
          >
            <UserPlus className="w-3.5 h-3.5" />
            <span>+ Walk-in Registration</span>
          </button>

          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-semibold transition-colors cursor-pointer border border-slate-200"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin text-[#0A3D27]' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Search Bar & Filter Controls */}
      <div className="bg-white border border-slate-200/90 rounded-2xl p-4 space-y-4 shadow-xs">
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by delegate name, email, phone, company, collection code (e.g. WIK-), or badge token..."
              className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl pl-10 pr-4 py-2 text-xs text-slate-900 placeholder-slate-400 focus:outline-none focus:border-[#0A3D27] focus:ring-1 focus:ring-[#0A3D27]"
            />
          </div>
          <button
            type="submit"
            className="px-5 py-2 bg-[#0A3D27] hover:bg-[#062B1B] text-white font-bold rounded-xl text-xs transition-colors flex items-center gap-1.5 cursor-pointer shadow-xs"
          >
            <Search className="w-3.5 h-3.5" />
            <span>Search</span>
          </button>
          {submittedQuery && (
            <button
              type="button"
              onClick={() => {
                setSearchInput('');
                setSubmittedQuery('');
              }}
              className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl text-xs transition-colors cursor-pointer border border-slate-200"
            >
              Clear
            </button>
          )}
        </form>

        {/* Local Filter Pills */}
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-slate-500 text-[11px] font-semibold uppercase">Role:</span>
            {[
              { id: 'all', label: 'All' },
              { id: 'vip', label: 'VIP' },
              { id: 'speaker', label: 'Speaker' },
              { id: 'exhibitor', label: 'Exhibitor' },
              { id: 'delegate', label: 'Delegate' },
              { id: 'conference_delegate', label: 'Conf. Delegate' },
              { id: 'trade_visitor', label: 'Trade Visitor' },
              { id: 'media', label: 'Media' },
              { id: 'organiser', label: 'Organiser' },
              { id: 'student_innovator', label: 'Student' },
            ].map((role) => (
              <button
                key={role.id}
                onClick={() => setFilterRole(role.id)}
                className={`px-2.5 py-1 rounded-lg text-xs font-semibold capitalize transition-all cursor-pointer ${
                  filterRole === role.id
                    ? 'bg-[#0A3D27] text-white shadow-xs'
                    : 'bg-slate-100 text-slate-600 hover:text-slate-900'
                }`}
              >
                {role.label}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-slate-500 text-[11px] font-semibold uppercase">Ingress:</span>
              {(['all', 'checked_in', 'pending'] as const).map((st) => (
                <button
                  key={st}
                  onClick={() => setFilterStatus(st)}
                  className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                    filterStatus === st
                      ? 'bg-[#0A3D27] text-white shadow-xs'
                      : 'bg-slate-100 text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {st === 'all' ? 'All' : st === 'checked_in' ? 'Admitted' : 'Pending'}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-slate-500 text-[11px] font-semibold uppercase">Badge Kit:</span>
              {(['all', 'collected', 'pending'] as const).map((st) => (
                <button
                  key={st}
                  onClick={() => setFilterKit(st)}
                  className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                    filterKit === st
                      ? 'bg-[#0A3D27] text-white shadow-xs'
                      : 'bg-slate-100 text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {st === 'all' ? 'All' : st === 'collected' ? 'Issued' : 'Awaiting Kit'}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Results Table / Card List */}
      <div className="bg-white border border-slate-200/90 rounded-2xl overflow-hidden shadow-xs">
        {isLoading ? (
          <div className="p-12 text-center text-slate-500 text-xs flex items-center justify-center gap-2">
            <RefreshCw className="w-4 h-4 animate-spin text-[#0A3D27]" />
            <span>Loading reception desk records...</span>
          </div>
        ) : filteredList.length === 0 ? (
          <div className="p-12 text-center text-slate-500 space-y-2">
            <Ticket className="w-8 h-8 mx-auto text-slate-400 mb-2" />
            <p className="text-sm font-semibold text-slate-900">No matching attendees found</p>
            <p className="text-xs text-slate-500 max-w-md mx-auto">
              {submittedQuery
                ? `No attendee records matched "${submittedQuery}". Try searching with a different name, phone, or token.`
                : 'No attendees currently match the selected filters.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-[11px] font-semibold text-slate-600 uppercase tracking-wider">
                  <th className="py-3 px-4">Delegate & Role</th>
                  <th className="py-3 px-4">Organization & Title</th>
                  <th className="py-3 px-4">Contact & Code</th>
                  <th className="py-3 px-4">Kit & Ingress</th>
                  <th className="py-3 px-4 text-right">Desk Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-700">
                {filteredList.map((item) => {
                  const attendee = item.attendee;
                  const activeCode = item.activeCredential?.badgeCode || attendee.qrToken;
                  const isHandedOver = item.handover?.badgeHandedOver;
                  const isVip = attendee.primaryRole === 'vip' || attendee.tier === 'vip';
                  const isPaymentUnverified = Boolean(
                    attendee.paymentStatus && attendee.paymentStatus !== 'paid'
                  );

                  return (
                    <tr key={attendee.id} className="hover:bg-slate-50/80 transition-colors">
                      {/* Delegate & Role */}
                      <td className="py-3.5 px-4">
                        <div className="space-y-1">
                          <div className="font-bold text-slate-900 text-sm flex items-center gap-2">
                            <span>{attendee.fullName}</span>
                            <button
                              onClick={() => openEditModal(item)}
                              title="Fix Spelling / Details"
                              className="text-slate-400 hover:text-[#0A3D27] transition-colors cursor-pointer"
                            >
                              <Edit3 className="w-3 h-3" />
                            </button>
                          </div>
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span
                              className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider border ${
                                item.theme?.badgeClass || 'bg-slate-100 text-slate-700 border-slate-200'
                              }`}
                            >
                              {item.theme?.label || attendee.primaryRole || attendee.tier}
                            </span>
                            {item.roles && item.roles.length > 1 && (
                              <span className="text-[10px] font-semibold text-slate-500">
                                +{item.roles.length - 1} role{item.roles.length > 2 ? 's' : ''}
                              </span>
                            )}
                            {isPaymentUnverified && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-300 text-amber-800 text-[10px] font-black uppercase tracking-wider">
                                <AlertTriangle className="w-3 h-3 text-amber-600" />
                                <span>
                                  {attendee.paymentStatus === 'refunded'
                                    ? 'Refunded'
                                    : `Payment ${attendee.paymentStatus}`}
                                </span>
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Organization & Title */}
                      <td className="py-3.5 px-4">
                        <div className="space-y-0.5">
                          <div className="text-slate-800 font-medium">
                            {attendee.companyName || 'Individual Delegate'}
                          </div>
                          {attendee.jobTitle && (
                            <div className="text-[11px] text-slate-500">{attendee.jobTitle}</div>
                          )}
                        </div>
                      </td>

                      {/* Contact & Code */}
                      <td className="py-3.5 px-4">
                        <div className="space-y-1 text-slate-600">
                          <div className="flex items-center gap-1.5">
                            <Mail className="w-3 h-3 text-slate-400 shrink-0" />
                            <span className="truncate max-w-[160px]">{attendee.email}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <Phone className="w-3 h-3 text-slate-400 shrink-0" />
                            <span>{attendee.phone}</span>
                          </div>
                          {activeCode && (
                            <div className="flex items-center gap-1.5 text-[10px] font-mono text-slate-500 pt-0.5">
                              <span>Code: {activeCode}</span>
                              <button
                                onClick={() => handleCopy(activeCode)}
                                title="Copy Pass Token"
                                className="text-slate-400 hover:text-[#0A3D27] cursor-pointer"
                              >
                                {copiedToken === activeCode ? (
                                  <Check className="w-3 h-3 text-[#0A3D27]" />
                                ) : (
                                  <Copy className="w-3 h-3" />
                                )}
                              </button>
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Kit & Ingress Status */}
                      <td className="py-3.5 px-4">
                        <div className="space-y-1.5">
                          {/* Handover Chip */}
                          <div>
                            {isHandedOver ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-[#0A3D27] text-[10px] font-bold">
                                <PackageCheck className="w-3 h-3" />
                                <span>Kit Handed Over</span>
                              </span>
                            ) : isPaymentUnverified ? (
                              <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-50 border border-rose-200 text-rose-700 text-[10px] font-bold"
                                title="Handover blocked until payment is verified"
                              >
                                <AlertTriangle className="w-3 h-3" />
                                <span>Payment Unverified • Blocked</span>
                              </span>
                            ) : (
                              <button
                                onClick={() =>
                                  handoverMutation.mutate({
                                    attendeeId: attendee.id,
                                    delegateKitIssued: true,
                                    vipKitIssued: isVip,
                                  })
                                }
                                disabled={handoverMutation.isPending}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-800 text-[10px] font-bold cursor-pointer transition-colors"
                                title="Click to record badge & kit handover"
                              >
                                <Clock className="w-3 h-3" />
                                <span>Hand Over Kit</span>
                              </button>
                            )}
                          </div>

                          {/* Ingress status */}
                          <div>
                            {attendee.paymentStatus === 'refunded' ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-50 border border-rose-200 text-rose-700 text-[10px] font-bold">
                                <X className="w-3 h-3" />
                                <span>Voided</span>
                              </span>
                            ) : attendee.checkedInAt ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-[#0A3D27] text-[10px] font-bold">
                                <CheckCircle2 className="w-3 h-3" />
                                <span>Admitted {new Date(attendee.checkedInAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200 text-slate-500 text-[10px] font-medium">
                                <Clock className="w-3 h-3" />
                                <span>Awaiting Ingress</span>
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Desk Actions */}
                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {/* Reprint Badge Button */}
                          <button
                            type="button"
                            disabled={isPaymentUnverified}
                            onClick={() => {
                              setReprintAttendee(item);
                              setReprintReason('damaged');
                            }}
                            className="px-2 py-1.5 bg-slate-100 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed text-slate-700 border border-slate-200 rounded-xl text-xs font-semibold transition-all flex items-center gap-1 cursor-pointer shadow-xs"
                            title={
                              isPaymentUnverified
                                ? 'Blocked until payment is verified'
                                : 'Replace / Reprint Badge'
                            }
                          >
                            <RefreshCw className="w-3 h-3 text-[#0A3D27]" />
                            <span>Reprint</span>
                          </button>

                          {/* Print 4x6 Badge Button */}
                          <button
                            disabled={isPaymentUnverified}
                            onClick={() =>
                              setSelectedAttendeeForBadge({
                                id: attendee.id,
                                fullName: attendee.fullName,
                                companyName: attendee.companyName,
                                jobTitle: attendee.jobTitle,
                                avatarUrl: (attendee as any).avatarUrl || null,
                                badgeCode: activeCode || `IIAB27-${attendee.id.slice(0, 8).toUpperCase()}`,
                                qrToken: activeCode,
                                primaryRole: item.theme?.role || attendee.primaryRole || attendee.tier,
                                roles: item.roles?.map((r) => r.role) || [],
                                stallNumber: (item.roles as any[])?.find((r) => r.stallNumber)?.stallNumber || null,
                                nfcTagId: item.activeCredential?.nfcTagId || null,
                                ticketType: attendee.ticketType,
                                tier: attendee.tier,
                                theme: item.theme,
                                paymentStatus: attendee.paymentStatus,
                              })
                            }
                            className="px-2.5 py-1.5 bg-[#FAF5EA] hover:bg-[#F3E8CE] disabled:opacity-40 disabled:cursor-not-allowed text-[#785613] border border-[#E6C687] rounded-xl text-xs font-bold transition-all flex items-center gap-1 cursor-pointer shadow-xs"
                            title={
                              isPaymentUnverified
                                ? 'Blocked until payment is verified'
                                : 'Preview & Print 4×6 Physical Badge'
                            }
                          >
                            <Printer className="w-3.5 h-3.5" />
                            <span>Print 4×6</span>
                          </button>

                          {attendee.email && attendee.paymentStatus !== 'refunded' && (
                            <a
                              href={`/my-iiab?email=${encodeURIComponent(attendee.email)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1.5 bg-slate-100 hover:bg-slate-200 text-slate-600 hover:text-slate-900 border border-slate-200 rounded-xl transition-colors cursor-pointer"
                              title="Open Attendee Portal in New Tab"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* MODAL 1: Walk-In Registration Modal */}
      {isWalkInOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto animate-in fade-in duration-200">
          <div className="bg-white border border-slate-200 rounded-3xl shadow-2xl max-w-xl w-full p-6 space-y-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <div className="flex items-center gap-2">
                <span className="p-1.5 rounded-lg bg-[#0A3D27]/10 text-[#0A3D27]">
                  <UserPlus className="w-4 h-4" />
                </span>
                <h3 className="text-base font-black text-slate-900">Onsite Walk-In Delegate Registration</h3>
              </div>
              <button
                onClick={() => setIsWalkInOpen(false)}
                className="text-slate-400 hover:text-slate-700 p-1 rounded-lg hover:bg-slate-100 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                walkInMutation.mutate(walkInForm);
              }}
              className="space-y-4 text-xs"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="font-bold text-slate-700">Full Name *</label>
                  <input
                    type="text"
                    required
                    value={walkInForm.fullName}
                    onChange={(e) => setWalkInForm({ ...walkInForm, fullName: e.target.value })}
                    placeholder="e.g. Ramesh Kumar"
                    className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-3 py-2 text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                  />
                </div>
                <div className="space-y-1">
                  <label className="font-bold text-slate-700">Email Address *</label>
                  <input
                    type="email"
                    required
                    value={walkInForm.email}
                    onChange={(e) => setWalkInForm({ ...walkInForm, email: e.target.value })}
                    placeholder="e.g. ramesh@agri-corp.in"
                    className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-3 py-2 text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="font-bold text-slate-700">Phone Number *</label>
                  <input
                    type="tel"
                    required
                    value={walkInForm.phone}
                    onChange={(e) => setWalkInForm({ ...walkInForm, phone: e.target.value })}
                    placeholder="e.g. +91 98765 43210"
                    className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-3 py-2 text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                  />
                </div>
                <div className="space-y-1">
                  <label className="font-bold text-slate-700">Company / Organization</label>
                  <input
                    type="text"
                    value={walkInForm.companyName}
                    onChange={(e) => setWalkInForm({ ...walkInForm, companyName: e.target.value })}
                    placeholder="e.g. Mahadhan Agri Solutions"
                    className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-3 py-2 text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="font-bold text-slate-700">Job Title / Designation</label>
                  <input
                    type="text"
                    value={walkInForm.jobTitle}
                    onChange={(e) => setWalkInForm({ ...walkInForm, jobTitle: e.target.value })}
                    placeholder="e.g. Managing Director"
                    className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-3 py-2 text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                  />
                </div>
                <div className="space-y-1">
                  <label className="font-bold text-slate-700">Primary Role / Credential Tier</label>
                  <select
                    value={walkInForm.primaryRole}
                    onChange={(e) => setWalkInForm({ ...walkInForm, primaryRole: e.target.value })}
                    className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-3 py-2 text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                  >
                    <option value="trade_visitor">Trade Visitor (Free)</option>
                    <option value="conference_delegate">Industry Professional (₹2,500 + GST)</option>
                    <option value="vip">VIP Executive Pass (₹6,000 + GST / VIP)</option>
                    <option value="speaker">Speaker / Keynote Guest (Complimentary)</option>
                    <option value="exhibitor">Exhibitor Staff Pass (Complimentary)</option>
                    <option value="media">Media / Press Accreditation (Complimentary)</option>
                    <option value="student_innovator">Student Innovator (Complimentary)</option>
                  </select>
                </div>
              </div>

              {/* Payment Details */}
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-3.5 space-y-3">
                <div className="flex items-center gap-1.5 text-xs font-bold text-slate-900">
                  <CreditCard className="w-4 h-4 text-[#0A3D27]" />
                  <span>On-Site Payment Details (SAC 998596 • 18% GST)</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="font-semibold text-slate-600">Payment Collection Method</label>
                    <select
                      value={walkInForm.paymentMethod}
                      onChange={(e) => setWalkInForm({ ...walkInForm, paymentMethod: e.target.value })}
                      className="w-full bg-white border border-slate-300 rounded-xl px-3 py-1.5 text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                    >
                      <option value="pos">POS Machine (Card Terminal)</option>
                      <option value="upi">UPI Standee QR Code</option>
                      <option value="cash">Cash Collected at Desk</option>
                      <option value="complimentary">Complimentary / Invited Guest</option>
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="font-semibold text-slate-600">POS Slip RRN / UPI Txn ID</label>
                    <input
                      type="text"
                      value={walkInForm.paymentReference}
                      onChange={(e) => setWalkInForm({ ...walkInForm, paymentReference: e.target.value })}
                      placeholder="e.g. POS-98124 / UPI-837482"
                      className="w-full bg-white border border-slate-300 rounded-xl px-3 py-1.5 text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="font-semibold text-slate-600">Company GSTIN (For B2B Tax Invoice - Optional)</label>
                  <input
                    type="text"
                    value={walkInForm.gstNumber}
                    onChange={(e) => setWalkInForm({ ...walkInForm, gstNumber: e.target.value.toUpperCase() })}
                    placeholder="e.g. 27AABCU9603R1ZX"
                    className="w-full bg-white border border-slate-300 rounded-xl px-3 py-1.5 text-slate-900 uppercase font-mono focus:outline-none focus:border-[#0A3D27]"
                  />
                </div>
              </div>

              <div className="border-t border-slate-200 pt-3 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsWalkInOpen(false)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold cursor-pointer border border-slate-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={walkInMutation.isPending}
                  className="px-5 py-2 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-xl font-bold transition-all shadow-xs cursor-pointer flex items-center gap-1.5 disabled:opacity-50"
                >
                  {walkInMutation.isPending && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                  <span>Register & Issue Badge</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 2: Badge Reprint Modal */}
      {reprintAttendee && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto animate-in fade-in duration-200">
          <div className="bg-white border border-slate-200 rounded-3xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <div className="flex items-center gap-2">
                <span className="p-1.5 rounded-lg bg-amber-500/10 text-amber-700">
                  <RefreshCw className="w-4 h-4" />
                </span>
                <h3 className="text-base font-black text-slate-900">Reprint Delegate Badge</h3>
              </div>
              <button
                onClick={() => setReprintAttendee(null)}
                className="text-slate-400 hover:text-slate-700 p-1 rounded-lg hover:bg-slate-100 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 text-xs text-amber-800 space-y-1">
              <p className="font-bold flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" />
                <span>Replacement Notice</span>
              </p>
              <p>
                Reprinting for <span className="font-bold text-slate-900">{reprintAttendee.attendee.fullName}</span> will invalidate credential{' '}
                <span className="font-mono font-bold">{reprintAttendee.activeCredential?.badgeCode || reprintAttendee.attendee.qrToken}</span> and immediately issue a new active badge code.
              </p>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                reprintMutation.mutate({
                  attendeeId: reprintAttendee.attendee.id,
                  reason: reprintReason,
                  notes: reprintNotes,
                });
              }}
              className="space-y-3 text-xs"
            >
              <div className="space-y-1">
                <label className="font-bold text-slate-700">Reprint Reason *</label>
                <select
                  value={reprintReason}
                  onChange={(e) => setReprintReason(e.target.value as any)}
                  className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-3 py-2 text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                >
                  <option value="damaged">Damaged Badge / Barcode Unreadable</option>
                  <option value="lost">Lost Badge on Site</option>
                  <option value="spelling_fix">Name or Company Spelling Fix</option>
                  <option value="role_change">Role / Pass Tier Upgrade</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="font-bold text-slate-700">Internal Desk Notes (Optional)</label>
                <input
                  type="text"
                  value={reprintNotes}
                  onChange={(e) => setReprintNotes(e.target.value)}
                  placeholder="e.g. Reprint requested at Pavilion 3 counter 2"
                  className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-3 py-2 text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                />
              </div>

              <div className="border-t border-slate-200 pt-3 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setReprintAttendee(null)}
                  className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold cursor-pointer border border-slate-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={reprintMutation.isPending}
                  className="px-4 py-2 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-xl font-bold transition-all shadow-xs cursor-pointer flex items-center gap-1.5 disabled:opacity-50"
                >
                  {reprintMutation.isPending && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                  <span>Generate & Print New Badge</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 3: Attendee Spelling & Title Edit Modal */}
      {editAttendee && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto animate-in fade-in duration-200">
          <div className="bg-white border border-slate-200 rounded-3xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <div className="flex items-center gap-2">
                <span className="p-1.5 rounded-lg bg-[#0A3D27]/10 text-[#0A3D27]">
                  <Edit3 className="w-4 h-4" />
                </span>
                <h3 className="text-base font-black text-slate-900">Correct Attendee Details</h3>
              </div>
              <button
                onClick={() => setEditAttendee(null)}
                className="text-slate-400 hover:text-slate-700 p-1 rounded-lg hover:bg-slate-100 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                updateAttendeeMutation.mutate({
                  attendeeId: editAttendee.attendee.id,
                  fullName: editFullName.trim(),
                  companyName: editCompanyName.trim(),
                  jobTitle: editJobTitle.trim(),
                });
              }}
              className="space-y-3 text-xs"
            >
              <div className="space-y-1">
                <label className="font-bold text-slate-700">Full Name *</label>
                <input
                  type="text"
                  required
                  value={editFullName}
                  onChange={(e) => setEditFullName(e.target.value)}
                  className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-3 py-2 text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                />
              </div>

              <div className="space-y-1">
                <label className="font-bold text-slate-700">Organization / Company</label>
                <input
                  type="text"
                  value={editCompanyName}
                  onChange={(e) => setEditCompanyName(e.target.value)}
                  className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-3 py-2 text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                />
              </div>

              <div className="space-y-1">
                <label className="font-bold text-slate-700">Job Title / Designation</label>
                <input
                  type="text"
                  value={editJobTitle}
                  onChange={(e) => setEditJobTitle(e.target.value)}
                  className="w-full bg-[#F8F9F6] border border-slate-300 rounded-xl px-3 py-2 text-slate-900 focus:outline-none focus:border-[#0A3D27]"
                />
              </div>

              <div className="border-t border-slate-200 pt-3 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setEditAttendee(null)}
                  className="px-3.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold cursor-pointer border border-slate-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateAttendeeMutation.isPending}
                  className="px-4 py-2 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-xl font-bold transition-all shadow-xs cursor-pointer flex items-center gap-1.5 disabled:opacity-50"
                >
                  {updateAttendeeMutation.isPending && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                  <span>Save Changes</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 4: 4x6 Physical Badge Print Modal */}
      {selectedAttendeeForBadge && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto animate-in fade-in duration-200 print:static print:bg-transparent print:p-0 print:block">
          <style>{`
            @media print {
              @page {
                size: 4in 6in;
                margin: 0;
              }
              body * {
                visibility: hidden !important;
              }
              #helpdesk-4x6-print-root,
              #helpdesk-4x6-print-root * {
                visibility: visible !important;
              }
              #helpdesk-4x6-print-root {
                position: fixed !important;
                left: 0 !important;
                top: 0 !important;
                width: 4in !important;
                height: 6in !important;
                margin: 0 !important;
                padding: 0 !important;
                display: flex !important;
                align-items: flex-start !important;
                justify-content: center !important;
                background: #ffffff !important;
              }
            }
          `}</style>
          <div className="bg-white border border-slate-200 rounded-3xl shadow-2xl max-w-lg w-full p-6 space-y-6 max-h-[92vh] overflow-y-auto print:border-0 print:shadow-none print:p-0 print:max-h-none print:overflow-visible">
            <div className="flex items-center justify-between border-b border-slate-200 pb-4 print:hidden">
              <div className="flex items-center gap-2">
                <Printer className="w-5 h-5 text-[#0A3D27]" />
                <div>
                  <h3 className="text-base font-black text-slate-900">
                    4×6 Physical Badge Print Preview
                  </h3>
                  <p className="text-[11px] text-slate-500">
                    Official 4in × 6in thermal/cardstock badge with QR code & NFC target
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedAttendeeForBadge(null)}
                className="text-slate-400 hover:text-slate-700 p-1 rounded-lg hover:bg-slate-100 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* 4x6 Physical Badge Component */}
            <div id="helpdesk-4x6-print-root" className="flex justify-center">
              <BadgeCard4x6
                badge={{
                  id: selectedAttendeeForBadge.id,
                  badgeCode:
                    selectedAttendeeForBadge.badgeCode ||
                    selectedAttendeeForBadge.qrToken ||
                    'IIAB27-PASS',
                  fullName: selectedAttendeeForBadge.fullName,
                  companyName: selectedAttendeeForBadge.companyName,
                  jobTitle: selectedAttendeeForBadge.jobTitle,
                  avatarUrl: selectedAttendeeForBadge.avatarUrl,
                  primaryRole:
                    selectedAttendeeForBadge.primaryRole ||
                    selectedAttendeeForBadge.tier ||
                    'trade_visitor',
                  roles: selectedAttendeeForBadge.roles || [],
                  stallNumber: selectedAttendeeForBadge.stallNumber || null,
                  nfcTagId: selectedAttendeeForBadge.nfcTagId || null,
                  ticketType: selectedAttendeeForBadge.ticketType,
                  tier: selectedAttendeeForBadge.tier,
                  theme: selectedAttendeeForBadge.theme,
                }}
                allowFlip={true}
              />
            </div>

            {/* Print & Action Buttons */}
            <div className="border-t border-slate-200 pt-4 flex items-center justify-between gap-3 print:hidden">
              <span className="text-xs font-mono text-slate-500">
                Code: {selectedAttendeeForBadge.badgeCode || selectedAttendeeForBadge.qrToken || 'IIAB27'}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => window.print()}
                  className="px-4 py-2 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shadow-xs"
                >
                  <Printer className="w-4 h-4" />
                  <span>Print 4×6 Badge</span>
                </button>
                <button
                  onClick={() => setSelectedAttendeeForBadge(null)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-semibold cursor-pointer border border-slate-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminHelpdesk;
